import type { MapLocation, MarkerStatus } from "@/components/admin/AdminMap";
import { isSnowRemovalTask } from "@/lib/task-progress";

export interface SnowSessionRow {
  id: string;
  location_id: string | null;
  status: string | null;
  started_at: string | null;
  completed_at: string | null;
  assigned_staff_id: string | null;
  check_in_metadata?: { session_type?: string } | null;
  task_type?: string | null;
}

export type SnowMapBaseLocation = Pick<
  MapLocation,
  "id" | "address" | "latitude" | "longitude"
>;

/**
 * Option A: green = snow removed after cutoff; yellow = in progress; gray = none after cutoff.
 * Morning sessions before the cutoff do not count as green.
 */
export function buildSnowMapLocations(
  locations: SnowMapBaseLocation[],
  sessions: SnowSessionRow[],
  cutoff: Date,
  workerNameById: Record<string, string>
): MapLocation[] {
  const cutoffMs = cutoff.getTime();
  const byLocation = new Map<string, SnowSessionRow[]>();

  for (const session of sessions) {
    if (!session.location_id) continue;
    if (!isSnowRemovalTask(session)) continue;
    const existing = byLocation.get(session.location_id) ?? [];
    existing.push(session);
    byLocation.set(session.location_id, existing);
  }

  return locations.map((location) => {
    const locSessions = byLocation.get(location.id) ?? [];
    let lastCompletedAt: string | null = null;
    let lastCompletedMs = Number.NEGATIVE_INFINITY;
    let inProgressWorkerId: string | null = null;
    let hasInProgress = false;

    for (const session of locSessions) {
      if (session.status === "done" && session.completed_at) {
        const completedMs = new Date(session.completed_at).getTime();
        if (!Number.isNaN(completedMs) && completedMs >= cutoffMs && completedMs > lastCompletedMs) {
          lastCompletedMs = completedMs;
          lastCompletedAt = session.completed_at;
        }
      }
      if (session.status === "in_progress") {
        hasInProgress = true;
        if (session.assigned_staff_id) {
          inProgressWorkerId = session.assigned_staff_id;
        }
      }
    }

    let status: MarkerStatus;
    if (lastCompletedAt) {
      status = "green";
    } else if (hasInProgress) {
      status = "yellow";
    } else {
      status = "gray";
    }

    return {
      id: location.id,
      address: location.address,
      latitude: location.latitude,
      longitude: location.longitude,
      status,
      currentWorkerName: inProgressWorkerId
        ? workerNameById[inProgressWorkerId] ?? null
        : null,
      nextTaskName: null,
      taskProgress: null,
      lastCompletedAt,
    };
  });
}
